import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray } from '@angular/forms';
import { SyllabusQuery } from '@examdojo/category';
import { distinctUntilDeepChanged, shareOneReplay } from '@examdojo/core/rxjs';
import { isNullish } from '@examdojo/core/util/nullish';
import {
  QuestionEntityQuery,
  QuestionEntityStoreModel,
  QuestionItemsFormService,
  TopLevelFormGroup,
} from '@examdojo/question';
import { QuestionImageCacheService } from '@examdojo/question-image';
import { DerivativeQuestionReviewType, QuestionType } from '@examdojo/review';
import { BehaviorSubject, combineLatest, from, map, merge, Observable, of, startWith, switchMap } from 'rxjs';
import { QuestionItemsCachingService } from '../question-items-caching/question-items-caching.service';
import {
  QuestionEditorCachingConfig,
  QuestionEditorCombinedState,
} from '../question-items-caching/question-items-caching.types';
import { getQuestionReviewConfig } from './derivative-question-validation.model';

@Injectable()
export class DerivativeQuestionEditorItemsFormService extends QuestionItemsFormService {
  constructor(
    private readonly questionQuery: QuestionEntityQuery,
    private readonly questionItemsCachingService: QuestionItemsCachingService,
    private readonly syllabusQuery: SyllabusQuery,
    protected override readonly questionImageCacheService: QuestionImageCacheService,
  ) {
    super();

    this.setupAutoSave();
  }

  readonly form = new FormArray<TopLevelFormGroup>([]);

  readonly mode$$ = new BehaviorSubject<DerivativeQuestionReviewType | null>(null);

  readonly combinedState$: Observable<QuestionEditorCombinedState | null> = combineLatest({
    questionId: this.questionQuery.selectActiveId(),
    mode: this.mode$$,
    syllabus: this.syllabusQuery.active$,
    editingOptions: this.mode$$.pipe(map((mode) => mode && getQuestionReviewConfig(mode))),
  }).pipe(
    switchMap((state) => {
      const { mode, syllabus, editingOptions, questionId } = state;
      if (!mode || !syllabus || !editingOptions || isNullish(questionId)) {
        return of(null);
      }

      const questionEditorCachingConfig: QuestionEditorCachingConfig = {
        ...state,
        questionType: QuestionType.Derivative,
        syllabusId: syllabus.id,
        mode,
        questionId,
        markCalculationMethod: syllabus.mark_calculation_method ?? 'ADDITIVE',
        freshQuestionItems: this.getFreshQuestionItems(questionId),
      };

      return merge(
        of(null),
        from(this.loadQuestionItems(questionEditorCachingConfig)).pipe(
          map((questionItems) => ({
            ...questionEditorCachingConfig,
            editingOptions,
            questionItems,
          })),
        ),
      );
    }),
    startWith(null),
    distinctUntilDeepChanged(),
    shareOneReplay(),
  );

  readonly editingOptions$ = this.combinedState$.pipe(map((state) => state?.editingOptions ?? null));
  readonly questionItems$ = this.combinedState$.pipe(map((state) => state?.freshQuestionItems ?? null));

  override readonly generateSolutionQuestionId$ = this.questionQuery.selectActiveId();

  private async loadQuestionItems(config: QuestionEditorCachingConfig) {
    if (isNullish(config.questionId)) {
      return null;
    }

    try {
      return await this.questionItemsCachingService.loadQuestionItemsWithCaching(config);
    } catch (error) {
      return this.questionItemsCachingService.handleLoadError(error, () =>
        this.getFreshQuestionItems(config.questionId),
      );
    }
  }

  private getFreshQuestionItems(id: QuestionEntityStoreModel['id']) {
    return this.questionQuery.getUIEntity(id)?.question.question_items ?? null;
  }

  private setupAutoSave() {
    this.questionItemsCachingService
      .setupAutoSave(this.combinedState$, this.currentState$)
      .pipe(takeUntilDestroyed())
      .subscribe();
  }
}
